import { useState, useEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    TextField,
    Button,
    Tooltip,
    Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import axiosInstance from '../../api/axiosInstance';
import swal from '../../utils/swal';
import Loader from '../../components/loading/Loader';
import './BuscarTurnos.css';

const SX_BTN_PRIMARY_CONTAINED = {
    minWidth: 0,
    px: 1.5,
    py: 0.9,
    backgroundColor: 'var(--color-primary)',
    '&:hover': { backgroundColor: '#078a87' },
};

const SX_BTN_PRIMARY_OUTLINED = {
    minWidth: 0,
    px: 1.5,
    py: 0.9,
    color: 'var(--color-primary)',
    borderColor: 'var(--color-primary)',
    '&:hover': { borderColor: 'var(--color-primary)', backgroundColor: 'rgba(9,166,163,0.08)' },
};

function BuscarTurnos() {
    const [fechaDesde, setFechaDesde] = useState(null);
    const [fechaHasta, setFechaHasta] = useState(null);
    const [sedeId, setSedeId] = useState('');
    const [especialidadId, setEspecialidadId] = useState('');
    const [practicaId, setPracticaId] = useState('');
    const [medicoSeleccionado, setMedicoSeleccionado] = useState(null);
    const [medicoInput, setMedicoInput] = useState('');

    const [sedes, setSedes] = useState([]);
    const [medicos, setMedicos] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);
    const [practicas, setPracticas] = useState([]);

    const [resultados, setResultados] = useState([]);
    const [buscado, setBuscado] = useState(false);
    const [cargando, setCargando] = useState(false);
    const [pagina, setPagina] = useState(1);
    const [paginacion, setPaginacion] = useState(null);
    const [reservando, setReservando] = useState(null);

    const dateOptions = { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' };

    // La prestación llega en el DTO como id (especialidad/práctica están embebidas
    // en el Médico, no son colecciones que el backend pueda popular). La resolvemos
    // contra los médicos ya cargados, misma fuente que alimenta los filtros.
    const nombrePrestacion = (id) =>
        especialidades.find(e => e._id === id)?.nombre
        ?? practicas.find(p => p._id === id)?.nombre
        ?? '-';

    useEffect(() => {
        async function cargarDatos() {
            const [sedesRes, medicosRes] = await Promise.all([
                axiosInstance.get('/sedes'),
                axiosInstance.get('/medicos'),
            ]);
            setSedes(sedesRes.data);

            const lista = medicosRes.data.data ?? [];
            setMedicos(lista);

            const esps = {};
            const pracs = {};
            lista.forEach(m => {
                m.especialidades?.forEach(e => { esps[e._id] = e; });
                m.practicas?.forEach(p => { pracs[p._id] = p; });
            });
            setEspecialidades(Object.values(esps));
            setPracticas(Object.values(pracs));
        }
        cargarDatos();
    }, []);

    async function buscar(page = 1) {
        setCargando(true);
        setBuscado(true);
        setPagina(page);
        try {
            const params = { page, limit: 4 };
            if (fechaDesde) params.fechaDesde = fechaDesde.toISOString();
            if (fechaHasta) params.fechaHasta = fechaHasta.toISOString();
            if (sedeId) params.sede = sedeId;
            if (especialidadId) params.especialidad = especialidadId;
            if (practicaId) params.practica = practicaId;
            if (medicoSeleccionado) params.profesional = medicoSeleccionado._id;

            const [res] = await Promise.all([
                axiosInstance.get('/turnos', { params }),
                new Promise(resolve => setTimeout(resolve, 3000)),
            ]);
            setResultados(res.data.data);
            setPaginacion(res.data.paginacion);
        } catch (err) {
            console.log(err);
        } finally {
            setCargando(false);
        }
    }

    function cambiarPagina(_, nuevaPagina) {
        buscar(nuevaPagina);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function limpiarFiltros() {
        setFechaDesde(null);
        setFechaHasta(null);
        setSedeId('');
        setEspecialidadId('');
        setPracticaId('');
        setMedicoSeleccionado(null);
        setMedicoInput('');
        setResultados([]);
        setBuscado(false);
        setPagina(1);
        setPaginacion(null);
    }

    async function reservar(turno) {
        const confirm = await swal.fire({
            title: '¿Reservar turno?',
            html: `
                <div style="text-align:left">
                    <p><b>Fecha:</b> ${new Date(turno.fechaHora).toLocaleString('es-AR', dateOptions)}</p>
                    <p><b>Profesional:</b> ${turno.profesional?.nombre ?? '-'}</p>
                    <p><b>Sede:</b> ${turno.sede?.nombre ?? '-'}</p>
                    <p><b>Servicio:</b> ${nombrePrestacion(turno.prestacion)}</p>
                    <p><b>Costo:</b> $ ${turno.costoEstimado}</p>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Reservar',
            cancelButtonText: 'Cancelar',
        });
        if (!confirm.isConfirmed) return;

        setReservando(turno._id);
        try {
            await axiosInstance.post(`/turnos/${turno._id}/reservar`);
            setResultados(prev => prev.filter(t => t._id !== turno._id));
            await swal.fire({
                icon: 'success',
                title: 'Turno reservado',
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (err) {
            const mensaje = err.response?.data?.message
                ?? 'No se pudo reservar el turno. Intentá de nuevo.';
            await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
        } finally {
            setReservando(null);
        }
    }

    return (
        <>
            <div className="buscar-turnos">

                <section className="buscar-turnos__section">
                    <header className="buscar-turnos__section-header">
                        BUSCAR TURNOS
                    </header>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div className="buscar-turnos__filtros">

                            <div className="buscar-turnos__inputs">

                                <DatePicker
                                    label="Desde"
                                    value={fechaDesde}
                                    onChange={setFechaDesde}
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />

                                <DatePicker
                                    label="Hasta"
                                    value={fechaHasta}
                                    onChange={setFechaHasta}
                                    minDate={fechaDesde ?? undefined}
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />

                                <FormControl size="small" fullWidth>
                                    <InputLabel>Sede</InputLabel>
                                    <Select
                                        value={sedeId}
                                        label="Sede"
                                        onChange={e => setSedeId(e.target.value)}
                                    >
                                        <MenuItem value="">Todas</MenuItem>
                                        {sedes.map(s => (
                                            <MenuItem key={s._id} value={s._id}>{s.nombre}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" fullWidth>
                                    <InputLabel>Especialidad</InputLabel>
                                    <Select
                                        value={especialidadId}
                                        label="Especialidad"
                                        onChange={e => setEspecialidadId(e.target.value)}
                                    >
                                        <MenuItem value="">Todas</MenuItem>
                                        {especialidades.map(e => (
                                            <MenuItem key={e._id} value={e._id}>{e.nombre}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" fullWidth>
                                    <InputLabel>Práctica</InputLabel>
                                    <Select
                                        value={practicaId}
                                        label="Práctica"
                                        onChange={e => setPracticaId(e.target.value)}
                                    >
                                        <MenuItem value="">Todas</MenuItem>
                                        {practicas.map(p => (
                                            <MenuItem key={p._id} value={p._id}>{p.nombre}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Autocomplete
                                    options={medicos}
                                    getOptionLabel={option => option.nombre}
                                    value={medicoSeleccionado}
                                    onChange={(_, newVal) => setMedicoSeleccionado(newVal)}
                                    inputValue={medicoInput}
                                    onInputChange={(_, newInput) => setMedicoInput(newInput)}
                                    isOptionEqualToValue={(option, value) => option._id === value._id}
                                    size="small"
                                    fullWidth
                                    renderInput={params => (
                                        <TextField {...params} label="Profesional" />
                                    )}
                                />

                            </div>

                            <div className="buscar-turnos__acciones">
                                <Tooltip title="Buscar">
                                    <Button
                                        variant="contained"
                                        onClick={() => buscar(1)}
                                        className="buscar-turnos__btn-accion"
                                        sx={SX_BTN_PRIMARY_CONTAINED}
                                    >
                                        <SearchIcon />
                                    </Button>
                                </Tooltip>

                                <Tooltip title="Limpiar filtros">
                                    <Button
                                        variant="outlined"
                                        onClick={limpiarFiltros}
                                        className="buscar-turnos__btn-accion"
                                        sx={SX_BTN_PRIMARY_OUTLINED}
                                    >
                                        <RefreshIcon />
                                    </Button>
                                </Tooltip>
                            </div>

                        </div>
                    </LocalizationProvider>
                </section>

                {!buscado && (
                    <div className="buscar-turnos__empty-state">
                        <ManageSearchIcon className="buscar-turnos__empty-icon" />
                        <p>Ingresa los filtros para encontrar los turnos</p>
                    </div>
                )}

                {buscado && (
                    <section className="buscar-turnos__section">
                        <header className="buscar-turnos__section-header">
                            RESULTADOS
                        </header>

                        {cargando ? (
                            <Loader mensaje="Buscando turnos..." />
                        ) : resultados.length === 0 ? (
                            <div className="buscar-turnos__empty-state">
                                <SearchOffIcon className="buscar-turnos__empty-icon" />
                                <p>No se encontraron turnos disponibles para los filtros seleccionados.</p>
                            </div>
                        ) : (
                            <div className="buscar-turnos__grid">
                                {resultados.map(turno => (
                                    <article key={turno._id} className="buscar-turnos__card">

                                        <div className="buscar-turnos__card-info">

                                            <div className="buscar-turnos__row">
                                                <EventIcon className="icon" />
                                                <span>{new Date(turno.fechaHora).toLocaleString('es-AR', dateOptions)}</span>
                                            </div>

                                            <div className="buscar-turnos__row">
                                                <PersonIcon className="icon" />
                                                <span>{turno.profesional?.nombre}</span>
                                            </div>

                                            <div className="buscar-turnos__row">
                                                <LocationOnIcon className="icon" />
                                                <span>{turno.sede?.nombre}</span>
                                            </div>

                                            <div className="buscar-turnos__row">
                                                <LocalHospitalIcon className="icon" />
                                                <span>
                                                    {nombrePrestacion(turno.prestacion)}
                                                </span>
                                            </div>

                                            <div className="buscar-turnos__costo">
                                                $ {turno.costoEstimado?.toLocaleString('es-AR') ?? '-'}
                                            </div>

                                        </div>

                                        <div className="buscar-turnos__card-actions">
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={reservando === turno._id ? null : <EventAvailableIcon />}
                                                onClick={() => reservar(turno)}
                                                disabled={reservando === turno._id}
                                                sx={SX_BTN_PRIMARY_CONTAINED}
                                            >
                                                {reservando === turno._id ? <Loader inline /> : 'Reservar'}
                                            </Button>
                                        </div>

                                    </article>
                                ))}
                            </div>
                        )}

                        {paginacion && paginacion.totalPaginas > 1 && (
                            <div className="buscar-turnos__paginacion">
                                <Pagination
                                    count={paginacion.totalPaginas}
                                    page={pagina}
                                    onChange={cambiarPagina}
                                    sx={{
                                        '& .MuiPaginationItem-root.Mui-selected': {
                                            backgroundColor: 'var(--color-primary)',
                                            color: '#fff',
                                        },
                                        '& .MuiPaginationItem-root.Mui-selected:hover': {
                                            backgroundColor: '#078a87',
                                        },
                                    }}
                                />
                            </div>
                        )}
                    </section>
                )}

            </div>
        </>
    );
}

export default BuscarTurnos;
